import { grey, red } from "@mui/material/colors";
import { createTheme, ThemeOptions } from "@mui/material/styles";

import React from "react";

declare module "@mui/material/styles" {
    interface TypographyVariants {
        body: React.CSSProperties;
        blockquote: React.CSSProperties;
        pre: React.CSSProperties;
        copyH2: React.CSSProperties;
        copyH3: React.CSSProperties;
        copyH4: React.CSSProperties;
        copyH5: React.CSSProperties;
        copyH6: React.CSSProperties;
        ul: React.CSSProperties;
        ol: React.CSSProperties;
        li: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        body?: React.CSSProperties;
        blockquote?: React.CSSProperties;
        pre?: React.CSSProperties;
        copyH2?: React.CSSProperties;
        copyH3?: React.CSSProperties;
        copyH4?: React.CSSProperties;
        copyH5?: React.CSSProperties;
        copyH6?: React.CSSProperties;
        ul?: React.CSSProperties;
        ol?: React.CSSProperties;
        li?: React.CSSProperties;
    }

    // Allow extra theme options for the palette
    interface PaletteOptions {
        typography?: {
            main?: string;
        };
        alt?: {
            main: string;
            contrastText: string;
        };
    }

    interface Palette {
        typography: {
            main: string;
        };
        alt: {
            main: string;
            contrastText: string;
        };
    }
}

declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        body: true;
        blockquote: true;
        pre: true;
        copyH2: true;
        copyH3: true;
        copyH4: true;
        copyH5: true;
        copyH6: true;
        ul: true;
        ol: true;
        li: true;

        // Turn off unused variants
        display2: false;
        display3: false;
        display4: false;
        body1: false;
        body2: false;
        subtitle1: false;
        subtitle2: false;
        overline: false;
        caption: false;
    }
}

type CodeThemeOptions = {
    color: string;
    background: string;
    comments: string;
};

const createCodeTheme = ({ comments, color, background }: CodeThemeOptions) => ({
    ".hljs": { color, background },
    ".hljs-comment, .hljs-quote": { color: comments, fontStyle: "italic" },
    ".hljs-doctag, .hljs-keyword, .hljs-formula": { color: "#c678dd" },
    ".hljs-section, .hljs-name, .hljs-selector-tag, .hljs-deletion, .hljs-subst": {
        color: "#e06c75"
    },
    ".hljs-literal": { color: "#56b6c2" },
    ".hljs-string, .hljs-regexp, .hljs-addition, .hljs-attribute, .hljs-meta .hljs-string":
        {
            color: "#98c379"
        },
    ".hljs-attr, .hljs-variable, .hljs-template-variable, .hljs-type, .hljs-selector-class, .hljs-selector-attr, .hljs-selector-pseudo, .hljs-number":
        {
            color: "#d19a66"
        },
    ".hljs-symbol, .hljs-bullet, .hljs-link, .hljs-meta, .hljs-selector-id, .hljs-title":
        {
            color: "#61aeee"
        },
    ".hljs-built_in, .hljs-title.class_, .hljs-class .hljs-title": {
        color: "#e6c07b"
    },
    ".hljs-emphasis": { fontStyle: "italic" },
    ".hljs-strong": { fontWeight: "bold" },
    ".hljs-link": { textDecoration: "underline" }
});

// todo: create css to jss converter
// themes from: https://github.com/highlightjs/highlight.js/tree/main/src/styles
const codeTheme = createCodeTheme({
    comments: "#5c6370",
    color: "#abb2bf",
    background: "#282c34"
});

const copyWidth = {
    maxWidth: "760px",
    marginLeft: "auto",
    marginRight: "auto"
};

const copyWidthWide = {
    ...copyWidth,
    maxWidth: "840px"
};

// Create a theme, so we can use the default theme to extend from
// e.g. breakpoints
const defaultTheme = createTheme({
    palette: {
        // https://www.canva.com/colors/color-wheel/
        primary: {
            main: "#ef283c", // "#F56371" // "#f50"
            dark: "#ac1d2c"
        },
        secondary: {
            main: "#28b3ef" // #63F5E7 #63F5E7
        },
        error: {
            main: red.A400
        },
        typography: {
            main: "#181818"
        },
        alt: {
            main: "#dcdcdc",
            contrastText: "#020202"
        }
    }
});

// Create a theme instance.
const theme: ThemeOptions = {
    palette: defaultTheme.palette,
    typography: {
        fontFamily: ["Quicksand", "sans-serif"].join(", "),
        h1: {
            fontSize: 48,
            fontWeight: 400,
            marginBottom: "1.25em",
            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: "1.25rem"
            }
        },
        h2: {
            fontSize: 32,
            fontWeight: 400,
            marginBottom: "1.25em"
        },
        h3: {
            fontSize: 28,
            fontWeight: 400,
            marginBottom: "1.25em"
        },
        h4: {
            fontSize: 21,
            fontWeight: 400,
            marginBottom: "1.25em"
        },
        h5: {
            fontSize: 18,
            fontWeight: 400,
            marginBottom: "1.25em"
        },
        h6: {
            fontSize: 16,
            fontWeight: 400,
            marginBottom: "1.25em"
        },
        copyH2: {
            fontFamily: ["Quicksand", "sans-serif"].join(","),
            fontSize: 32,
            fontWeight: 400,
            marginBottom: "1.25em",
            ...copyWidth
        },
        copyH3: {
            fontFamily: ["Quicksand", "sans-serif"].join(","),
            fontSize: 28,
            fontWeight: 400,
            marginBottom: "1.25em",
            ...copyWidth
        },
        copyH4: {
            fontFamily: ["Quicksand", "sans-serif"].join(","),
            fontSize: 21,
            fontWeight: 400,
            marginBottom: "1.25em",
            ...copyWidth
        },
        copyH5: {
            fontFamily: ["Quicksand", "sans-serif"].join(","),
            fontSize: 18,
            fontWeight: 400,
            marginBottom: "1.25em",
            ...copyWidth
        },
        copyH6: {
            fontFamily: ["Quicksand", "sans-serif"].join(","),
            fontSize: 16,
            fontWeight: 400,
            marginBottom: "1.25em",
            ...copyWidth
        },
        body: {
            fontFamily: ["Quicksand", "sans-serif"].join(","),
            fontSize: "1rem",
            fontWeight: 400,
            lineHeight: 1.5,
            marginBottom: "1.25em",
            letterSpacing: "0.00938em",
            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: "1rem"
            },
            ...copyWidth
        },
        ul: {
            fontFamily: ["Quicksand", "sans-serif"].join(","),
            fontSize: "1rem",
            fontWeight: 400,
            lineHeight: 1.5,
            marginBottom: "1.25em",
            letterSpacing: "0.00938em",
            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: "1rem"
            },
            ...copyWidth
        },
        ol: {
            fontFamily: ["Quicksand", "sans-serif"].join(","),
            fontSize: "1rem",
            fontWeight: 400,
            lineHeight: 1.5,
            marginBottom: "1.25em",
            letterSpacing: "0.00938em",
            [defaultTheme.breakpoints.down("sm")]: {
                fontSize: "1rem"
            },
            ...copyWidth
        },
        // We don't use body1, but it seems that other components
        // inherit the font from the page from it so we have to leave this in :(
        body1: undefined,
        body2: undefined,
        blockquote: {
            fontFamily: ["Quicksand", "sans-serif"].join(","),
            borderLeft: `10px solid`,
            borderLeftColor: defaultTheme.palette.primary.main,
            backgroundColor: grey[200],
            padding: defaultTheme.spacing(2),
            marginBottom: defaultTheme.spacing(2),
            ...copyWidthWide
        },

        pre: {
            ...copyWidthWide,
            color: "#e4e7ef",
            marginBottom: defaultTheme.spacing(2)
        },
        allVariants: {
            fontFamily: ["Quicksand", "sans-serif"].join(","),
            color: "#282828" // todo: move to theme?
        }
    },
    shape: {
        borderRadius: 0
    },

    components: {
        MuiTypography: {
            defaultProps: {
                variant: "body",
                variantMapping: {
                    body: "p",
                    blockquote: "blockquote",
                    pre: "pre",
                    copyH2: "h2",
                    copyH3: "h3",
                    copyH4: "h4",
                    copyH5: "h5",
                    copyH6: "h6",
                    ul: "ul",
                    ol: "ol",
                    li: "li"
                }
            }
        },
        MuiButton: {
            defaultProps: {},
            styleOverrides: {
                root: {
                    textTransform: "none"
                }
            },
            variants: []
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: defaultTheme.palette.alt.contrastText,
                    color: "#eee",
                    border: "none",
                    padding: 12,
                    margin: 12,
                    fontSize: "1rem"
                },
                arrow: {
                    color: defaultTheme.palette.alt.contrastText
                }
            }
        },
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    // set the font for all things. This is required when we turn off body1
                    fontFamily: ["Quicksand", "sans-serif"].join(",")
                },
                code: {
                    backgroundColor: "#ffe6e6",
                    color: "#ac1d2c",
                    padding: "2px 4px",
                    borderRadius: 3,
                    whiteSpace: "break-spaces"
                },
                pre: {
                    backgroundColor: "#282A36",
                    overflowX: "scroll",
                    borderRadius: 3,
                    padding: 10,
                    code: {
                        padding: 0,
                        color: "#F8F8F2",
                        backgroundColor: "#282A36",
                        whiteSpace: "pre",
                        ...codeTheme
                    }
                },
                ul: {
                    paddingLeft: "1em"
                },
                mark: {
                    // border: " 2px dotted #ac1d2c",
                    background: red[50],
                    borderRadius: 2
                }
            }
        }
    }
};

export default createTheme(theme);
